import React, { useEffect, useState } from "react";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, Info, Lock, PlayCircle } from "lucide-react";

const STEPS = [
  { key: "org",             label: "Organisation" },
  { key: "plan",            label: "Plan" },
  { key: "primary_contact", label: "Primary Contact" },
  { key: "org_admin",       label: "Organisation Admin" },
  { key: "first_site",      label: "First Site" },
  { key: "modules",         label: "Modules" },
  { key: "review",          label: "Review" },
];

const MODULE_LABELS = {
  emar: "eMAR", training: "Training & Compliance", outcomes: "Outcomes",
  family_portal: "Family Portal", ai_insights: "AI Insights",
  multi_site: "Multi-Site", sso: "SSO",
};

export default function PlatformOnboarding() {
  const { api } = usePlatformAuth();
  const [step, setStep] = useState(0);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    org: { name: "", slug: "", cqc_number: "" },
    plan_id: "",
    seat_limit: "",
    module_flags: {},
    primary_contact: { name: "", email: "", phone: "" },
    org_admin: { name: "", email: "" },
    first_site: { name: "", address: "", phone: "" },
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, [api]);

  const setPart = (k, patch) => setForm((f) => ({ ...f, [k]: { ...(f[k] || {}), ...patch } }));

  const runPreview = async () => {
    setBusy(true);
    setPreview(null);
    try {
      const body = { ...form, seat_limit: form.seat_limit === "" ? null : Number(form.seat_limit) };
      const r = await api.post("/onboarding/preview", body);
      setPreview(r.data);
      if (r.data.issues?.length) toast.error(`${r.data.issues.length} validation issue(s)`);
      else toast.success("Dry run passed — safe to review");
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!window.confirm("This will provision a new organisation and activate tenant enforcement. Continue?")) return;
    try {
      await api.post("/onboarding/commit", form);
      toast.success("Organisation created");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 423) {
        toast.error("Commit is currently locked", { description: typeof detail === "string" ? detail : "Ask a Platform Admin to enable ONBOARDING_COMMIT_ENABLED (audited)." });
      } else if (e?.response?.status === 403) {
        toast.error("Two-factor authentication required", { description: "Platform Admin MFA must be enrolled before committing a new organisation." });
      } else toast.error(detail || "Commit failed");
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl" data-testid="platform-onboarding">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Onboarding</div>
        <h1 className="text-2xl font-heading font-semibold" data-testid="onboarding-title">Customer Onboarding Wizard</h1>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl">
          Preview and validate every field before provisioning a new customer organisation.
          <b className="text-amber-300"> The Create action is gated by an audited server flag</b>{" "}
          — the dry-run writes nothing to the database.
        </p>
      </div>

      {/* Stepper */}
      <ol className="flex items-center gap-1 text-xs" data-testid="onboarding-stepper">
        {STEPS.map((s, i) => (
          <li key={s.key} className={`flex-1 flex items-center gap-2 ${i === step ? "text-emerald-300" : i < step ? "text-emerald-500" : "text-slate-500"}`} data-testid={`step-${s.key}`}>
            <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
              i === step ? "bg-emerald-500 text-white" : i < step ? "bg-emerald-800/60 text-emerald-300" : "bg-slate-800 text-slate-500"
            }`}>{i + 1}</span>
            <span className="truncate">{s.label}</span>
            {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-700 ml-auto" />}
          </li>
        ))}
      </ol>

      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-4" data-testid={`step-body-${STEPS[step].key}`}>
        {STEPS[step].key === "org" && (
          <>
            <StepHeading title="Organisation" hint="Legal name and CQC identifier." />
            <Field label="Legal name *"><input value={form.org.name} onChange={(e) => setPart("org", { name: e.target.value })} className={inputCls} data-testid="wizard-org-name" /></Field>
            <Field label="URL slug (optional)"><input value={form.org.slug} onChange={(e) => setPart("org", { slug: e.target.value })} className={inputCls} placeholder="acme-care" data-testid="wizard-org-slug" /></Field>
            <Field label="CQC number (if applicable)"><input value={form.org.cqc_number} onChange={(e) => setPart("org", { cqc_number: e.target.value })} className={inputCls} data-testid="wizard-org-cqc" /></Field>
          </>
        )}
        {STEPS[step].key === "plan" && (
          <>
            <StepHeading title="Plan" hint="Choose a starting plan — seat limit and module flags default from the plan and can be tuned later." />
            <div className="grid gap-2">
              {plans.filter((p) => p.id !== "founding-unlimited").map((p) => (
                <label key={p.id} className={`flex items-center gap-3 border rounded-md p-3 cursor-pointer ${form.plan_id === p.id ? "border-emerald-500 bg-emerald-500/10" : "border-slate-800 bg-slate-950"}`} data-testid={`wizard-plan-${p.id}`}>
                  <input type="radio" name="plan" value={p.id} checked={form.plan_id === p.id}
                         onChange={() => setForm((f) => ({ ...f, plan_id: p.id, seat_limit: p.seat_limit, module_flags: { ...(p.module_flags || {}) } }))} />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.seat_limit} seats · £{(p.price_pence / 100).toFixed(0)} / {p.interval}</div>
                  </div>
                </label>
              ))}
            </div>
            <Field label="Override seat limit (optional)">
              <input type="number" min={1} value={form.seat_limit} onChange={(e) => setForm({ ...form, seat_limit: e.target.value })} className={inputCls} data-testid="wizard-seat-limit" />
            </Field>
          </>
        )}
        {STEPS[step].key === "primary_contact" && (
          <>
            <StepHeading title="Primary contact" hint="Where the welcome email goes." />
            <Field label="Name"><input value={form.primary_contact.name} onChange={(e) => setPart("primary_contact", { name: e.target.value })} className={inputCls} data-testid="wizard-pc-name" /></Field>
            <Field label="Email *"><input type="email" value={form.primary_contact.email} onChange={(e) => setPart("primary_contact", { email: e.target.value })} className={inputCls} data-testid="wizard-pc-email" /></Field>
            <Field label="Phone"><input value={form.primary_contact.phone} onChange={(e) => setPart("primary_contact", { phone: e.target.value })} className={inputCls} data-testid="wizard-pc-phone" /></Field>
          </>
        )}
        {STEPS[step].key === "org_admin" && (
          <>
            <StepHeading title="Organisation admin" hint="First user in the new org. Receives password-setup link on commit. Never given cross-organisation access." />
            <Field label="Name *"><input value={form.org_admin.name} onChange={(e) => setPart("org_admin", { name: e.target.value })} className={inputCls} data-testid="wizard-oa-name" /></Field>
            <Field label="Email *"><input type="email" value={form.org_admin.email} onChange={(e) => setPart("org_admin", { email: e.target.value })} className={inputCls} data-testid="wizard-oa-email" /></Field>
          </>
        )}
        {STEPS[step].key === "first_site" && (
          <>
            <StepHeading title="First site / house" hint="The starting site inside the new org. Additional sites can be added later." />
            <Field label="Name *"><input value={form.first_site.name} onChange={(e) => setPart("first_site", { name: e.target.value })} className={inputCls} data-testid="wizard-site-name" /></Field>
            <Field label="Address"><input value={form.first_site.address} onChange={(e) => setPart("first_site", { address: e.target.value })} className={inputCls} data-testid="wizard-site-address" /></Field>
            <Field label="Phone"><input value={form.first_site.phone} onChange={(e) => setPart("first_site", { phone: e.target.value })} className={inputCls} /></Field>
          </>
        )}
        {STEPS[step].key === "modules" && (
          <>
            <StepHeading title="Modules" hint="Enable or disable modules for this organisation. Defaults come from the chosen plan." />
            <div className="grid md:grid-cols-2 gap-1.5" data-testid="wizard-modules">
              {Object.keys(MODULE_LABELS).map((k) => {
                const on = !!form.module_flags?.[k];
                return (
                  <label key={k} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${on ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-200" : "bg-slate-950 border-slate-800 text-slate-300"}`}>
                    <input type="checkbox" checked={on} onChange={(e) => setForm({
                      ...form, module_flags: { ...(form.module_flags || {}), [k]: e.target.checked },
                    })} data-testid={`wizard-module-${k}`} />
                    {MODULE_LABELS[k]}
                  </label>
                );
              })}
            </div>
          </>
        )}
        {STEPS[step].key === "review" && (
          <>
            <StepHeading title="Review" hint="Run a dry-run to validate the wizard and see exactly what would be created." />
            <button onClick={runPreview} disabled={busy}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
                    data-testid="wizard-run-preview">
              <PlayCircle className="w-4 h-4" /> {busy ? "Running…" : "Run dry-run preview"}
            </button>

            {preview && (
              <div className="mt-4 space-y-3 text-sm" data-testid="wizard-preview-result">
                {/* Issues */}
                {preview.issues?.length > 0 ? (
                  <div className="border border-rose-500/40 bg-rose-500/10 rounded-md p-3" data-testid="preview-issues">
                    <div className="flex items-center gap-2 text-rose-300 font-semibold text-xs uppercase tracking-widest">
                      <AlertTriangle className="w-3.5 h-3.5" /> {preview.issues.length} validation issue(s)
                    </div>
                    <ul className="text-xs mt-2 space-y-1 text-rose-200">
                      {preview.issues.map((i, ix) => (
                        <li key={ix}>• <b>{i.scope}.{i.field}</b> — {i.message}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="border border-emerald-500/40 bg-emerald-500/10 rounded-md p-3 text-emerald-300 text-xs inline-flex items-center gap-1.5" data-testid="preview-no-issues">
                    <CheckCircle2 className="w-3.5 h-3.5" /> No validation issues found
                  </div>
                )}

                {/* Conflicts */}
                {(preview.conflicts?.slug_conflict || preview.conflicts?.admin_email_conflict) && (
                  <div className="border border-amber-500/40 bg-amber-500/10 rounded-md p-3 text-amber-200 text-xs" data-testid="preview-conflicts">
                    <div className="font-semibold uppercase tracking-widest text-[10px]">Conflicts</div>
                    {preview.conflicts.slug_conflict && <div>• Slug already in use by another organisation.</div>}
                    {preview.conflicts.admin_email_conflict && <div>• Admin email already exists as a user account.</div>}
                  </div>
                )}

                {/* Would create */}
                <div className="border border-slate-800 bg-slate-950 rounded-md p-3" data-testid="preview-would-create">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Would create on commit</div>
                  <ul className="text-xs font-mono mt-2 space-y-1 text-slate-300">
                    {preview.would_create.map((w, i) => (
                      <li key={i}>
                        <span className="text-emerald-400">{w.op}</span>{" "}
                        <span className="text-slate-400">→ {w.target}</span>{" "}
                        <span className="text-slate-500">{JSON.stringify(w.detail)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`border rounded-md p-3 text-xs ${
                  preview.commit_enabled ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                                         : "border-amber-500/40 bg-amber-500/10 text-amber-200"
                }`} data-testid="preview-commit-status">
                  {preview.commit_enabled ? (
                    <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Commit is enabled — you may proceed. Multi-tenant enforcement will apply automatically.</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {preview.commit_locked_reason || "Commit is currently locked. Contact a Platform Admin to unlock."}</span>
                  )}
                </div>

                <button
                  onClick={commit}
                  disabled={!preview.commit_enabled}
                  data-testid="wizard-commit"
                  className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-white text-sm font-semibold
                             ${preview.commit_enabled ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 cursor-not-allowed opacity-60"}`}
                >
                  {preview.commit_enabled ? "Create Organisation" : "Create Organisation (locked)"}
                </button>
              </div>
            )}
          </>
        )}

        <div className="flex justify-between pt-4 border-t border-slate-800">
          <button disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="inline-flex items-center gap-1 h-8 px-3 text-xs text-slate-300 hover:text-white disabled:opacity-40"
                  data-testid="wizard-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-slate-800 hover:bg-slate-700 text-xs text-white"
                    data-testid="wizard-next">
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Info className="w-3.5 h-3.5" /> Preview writes nothing.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-9 px-3 rounded-md bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:outline-none text-sm text-slate-100";
function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</span>
      {children}
    </label>
  );
}
function StepHeading({ title, hint }) {
  return (
    <div>
      <div className="text-sm font-semibold text-slate-100">{title}</div>
      {hint && <div className="text-xs text-slate-500 mt-1 max-w-2xl">{hint}</div>}
    </div>
  );
}
